/**
 * The EventModel represents an actual event or competition created by an organizer.
 */
import Vue from "vue";
import { v4 as uuid4 } from 'uuid';
import Model, { ModelErrors } from './Model';
import DivisionModel, { DivisionData, create as createDivision } from "./DivisionModel";
import EventWaiverModel, { EventWaiverData, create as createWaiver } from "./EventWaiverModel";

export interface EventData {
  id?: number;
  title: string;
  content: string;
  reference?: string;
  starts?: string;
  ends?: string;
  timezone: string;
  deadline_submissions_utc: string;
  deadline_edit_utc: string;
  deadline_replace_video_utc: string;
  deadline_submissions_passed: boolean;
  deadline_edit_passed: boolean;
  deadline_replace_video_passed: boolean;
  force_custom_instructor: boolean;
  allow_donations: boolean;
  show_countdowns: boolean;
  spectator_division_id: number;
  has_custom_waiver: boolean;
  event_countdown_text: string;
  _embedded: {divisions: DivisionData[], custom_waiver?: EventWaiverData, meta: EventMeta}
  [propName: string]: unknown;
}

export interface EventCustomCountdown {
  title: string;
  description?: string;
  target_utc: string;
  ended_label?: string;
  background_color?: string;
}

export interface EventCheckoutRequirements {
  value: any;
  label: string;
}

export interface EventMeta {
  theme: {
    countdown_theme: string;
    countdown_event_ends_label: string;
    countdown_show_event_ends: boolean;
    countdown_registration_ends_label: string;
    countdown_show_registration_ends: boolean;
    countdown_replacement_ends_label: string;
    countdown_show_replacement_ends: boolean;
    register_event_button: string;
    login_event_button: string;
    header_my_entries: string;
  };

  checkout: {
    label_minimum_price: string;
    label_recommended_price: string;
    label_name_your_price: string;
    requirements: EventCheckoutRequirements[];
    text_unpaid_items_cart_header: string;
  };

  custom_countdowns: EventCustomCountdown[];

  divisions: {
    description: string;
  };

  permissions: {
    enabled: number[];
  }
}

interface EventLinks {
  event: string;
  hosted: string;
}

export default class EventModel extends Model<EventData, EventMeta> {
  divisions: DivisionModel[] = [];
  custom_waiver?: EventWaiverModel;
  links?: EventLinks;

  constructor(data?: EventData, errors?: ModelErrors) {
    super(data, errors);

    if (data?._embedded?.divisions) {
      for (const dData of data._embedded.divisions) {
        this.divisions.push(createDivision(dData));
      }
    }

    if (data?._embedded?.custom_waiver) {
      this.custom_waiver = createWaiver(data._embedded.custom_waiver);
    }

    if (data?._embedded?.meta) {
      this.setMeta(data._embedded.meta);
    }

    if (data?._links) {
      Vue.set(this, 'links', data._links);
    }
  }

  generateReference(): void {
    this.data.reference = uuid4();
  }

}

export function create(data?: EventData, errors?: ModelErrors): EventModel {
  return new EventModel(data, errors);
}
